import React from "react"
import {useTranslation} from "react-i18next"

// Statements
import {EcgStatement, groupedStatements, statementLabel, StatementLanguage, statementText} from "../ecgStatements"

// Styles
import useStyles from "./styles"

type StatementPaletteProps = {
    open: boolean
    language: StatementLanguage
    /** The findings text as it stands, used to mark what is already written. */
    value: string
    onInsert(statement: EcgStatement): void
    onClose(): void
}

/**
 * The full statement list, grouped the way the report is organised.
 *
 * This is the half of the feature that does not require knowing what to type.
 * The typeahead is faster once a technician knows the vocabulary; this is how
 * they learn it, and how anyone finds the statement they only need twice a
 * year.
 *
 * It STAYS OPEN after an insertion on purpose. A normal report carries a
 * rhythm, a conduction and a repolarisation line, and closing after each one
 * would mean opening it three times.
 *
 * Carrying `role="dialog"` is load-bearing beyond accessibility: the page's
 * keyboard shortcuts stand down while any dialog is mounted, so `1`–`6` cannot
 * fire a conclusion chip while someone is typing in the search box here.
 *
 * @param props
 */
export default function StatementPalette(props: StatementPaletteProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {open, language, value, onInsert, onClose} = props;

    const [query, setQuery] = React.useState("");
    const [activeIndex, setActiveIndex] = React.useState(0);
    const searchRef = React.useRef<HTMLInputElement | null>(null);
    const listRef = React.useRef<HTMLDivElement | null>(null);

    const groups = React.useMemo(() => groupedStatements(query, language), [query, language]);

    // one flat sequence for the keyboard, in the order the eye reads them
    const flat = React.useMemo(
        () => groups.reduce<EcgStatement[]>((all, group) => all.concat(group.statements), []),
        [groups],
    );

    const present = React.useMemo(() => {
        const lines = value.split("\n").map(line => line.trim().toLowerCase()).filter(line => line !== "");

        return (statement: EcgStatement) => {
            const text = statementText(statement, language).trim().toLowerCase();

            return statement.leads
                ? lines.some(line => line.startsWith(text))
                : lines.some(line => line === text)
        }
    }, [value, language]);

    React.useEffect(() => {
        if (open) {
            setQuery("");
            setActiveIndex(0)
        }
    }, [open]);

    React.useEffect(() => {
        if (open && searchRef.current !== null) {
            searchRef.current.focus()
        }
    }, [open]);

    // keep the highlighted row in view when the keyboard walks past the fold
    React.useEffect(() => {
        const list = listRef.current;

        if (list === null) {
            return
        }

        const active = list.querySelector("[data-active=\"true\"]");

        if (active !== null && typeof (active as HTMLElement).scrollIntoView === "function") {
            (active as HTMLElement).scrollIntoView({block: "nearest"})
        }
    }, [activeIndex, query]);

    if (!open) {
        return null
    }

    function insertAt(index: number) {
        const statement = flat[index];

        if (typeof statement !== "undefined") {
            onInsert(statement)
        }
    }

    function handleKeyDown(event: React.KeyboardEvent) {
        if (event.key === "Escape") {
            // the page also listens for Escape; without this it would clear the
            // caliper measurement behind the open palette
            event.stopPropagation();
            event.preventDefault();
            onClose();

            return
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex(index => (flat.length === 0 ? 0 : (index + 1) % flat.length));

            return
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex(index => (flat.length === 0 ? 0 : (index - 1 + flat.length) % flat.length));

            return
        }

        if (event.key === "Enter") {
            event.preventDefault();
            insertAt(activeIndex)
        }
    }

    let runningIndex = -1;

    return (
        <div
            className={styles.overlay}
            onMouseDown={event => {
                if (event.target === event.currentTarget) {
                    onClose()
                }
            }}
        >
            <div
                className={styles.card}
                role="dialog"
                aria-modal="true"
                aria-label={t("statements.title")}
                onKeyDown={handleKeyDown}
            >
                <div className={styles.head}>
                    <span className={styles.microlabel}>{t("statements.title")}</span>
                    <button type="button" className={styles.close} onClick={onClose} aria-label={t("modern.close")}>
                        ×
                    </button>
                </div>

                <input
                    ref={searchRef}
                    type="text"
                    className={styles.search}
                    value={query}
                    placeholder={t("statements.search")}
                    aria-label={t("statements.search")}
                    onChange={event => {
                        setQuery(event.target.value);
                        setActiveIndex(0)
                    }}
                />

                <div className={styles.list} ref={listRef}>
                    {groups.length === 0 && (
                        <p className={styles.empty}>{t("statements.empty")}</p>
                    )}

                    {groups.map(group => (
                        <section key={group.section} className={styles.group}>
                            <h3 className={styles.group_title}>{t(`statements.section.${group.section}`)}</h3>

                            {group.statements.map(statement => {
                                runningIndex += 1;

                                const index = runningIndex;
                                const added = present(statement);

                                return (
                                    <button
                                        key={statement.key}
                                        type="button"
                                        data-active={index === activeIndex}
                                        className={
                                            `${styles.row}` +
                                            `${index === activeIndex ? " " + styles.row__active : ""}` +
                                            `${added ? " " + styles.row__added : ""}`
                                        }
                                        /*
                                         * Out of the tab order on purpose:
                                         * this list is driven by arrows and
                                         * Enter from the search box, and a row
                                         * that could also take focus would let
                                         * Enter insert the focused row and the
                                         * highlighted row at once.
                                         */
                                        tabIndex={-1}
                                        // preventDefault keeps focus in the
                                        // search box; the insert hangs off
                                        // click, which is what a pointer and
                                        // an assistive click both produce
                                        onMouseDown={event => event.preventDefault()}
                                        onClick={() => onInsert(statement)}
                                        onMouseEnter={() => setActiveIndex(index)}
                                    >
                                        <span className={styles.row_text}>
                                            {statementLabel(statement, language)}
                                            {statement.leads && (
                                                <span className={styles.slot}>{t("statements.leads")}</span>
                                            )}
                                        </span>
                                        {added && <span className={styles.added}>{t("statements.added")}</span>}
                                    </button>
                                )
                            })}
                        </section>
                    ))}
                </div>

                <p className={styles.hint}>{t("statements.hint")}</p>
            </div>
        </div>
    )
}
